// packages/frontend/src/components/AdminDrawer.tsx
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import AssessmentIcon from '@mui/icons-material/Assessment';
import CampaignIcon from '@mui/icons-material/Campaign';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import GroupIcon from '@mui/icons-material/Group';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import SettingsIcon from '@mui/icons-material/Settings';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import { Box, Drawer, Fade, IconButton, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Tooltip, Typography } from '@mui/material';

interface AdminMenuItem {
  label: string;
  path: string;
  icon: React.ReactNode;
}

interface AdminMenuCategory {
  title: string;
  icon: React.ReactNode;
  color: string;
  items: AdminMenuItem[];
}

const AdminDrawer = () => {
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();

  const menuCategories: AdminMenuCategory[] = [
    {
      title: 'Promos',
      icon: <CampaignIcon />,
      color: '#FF6B6B',
      items: [
        {
          label: 'Bannière Promo',
          path: '/admin/promotional-banner',
          icon: <CampaignIcon sx={{ fontSize: 20 }} />,
        },
        {
          label: 'Message Accueil Agence',
          path: '/admin/agency-home-promo-message',
          icon: <CampaignIcon sx={{ fontSize: 20 }} />,
        },
      ],
    },
    {
      title: 'Gestion des agences',
      icon: <GroupIcon />,
      color: '#EF8F35',
      items: [
        {
          label: 'Tarifs Franchises',
          path: '/admin/rfa',
          icon: <SettingsIcon sx={{ fontSize: 20 }} />,
        },
        {
          label: 'Organisations / Personnes',
          path: '/admin/org-persons',
          icon: <GroupIcon sx={{ fontSize: 20 }} />,
        },
        {
          label: 'Feature flags',
          path: '/admin/feature-flags',
          icon: <ToggleOnIcon sx={{ fontSize: 20 }} />,
        },
      ],
    },
    {
      title: 'Facturation',
      icon: <ReceiptLongIcon />,
      color: '#95E1D3',
      items: [
        {
          label: 'Clients / Facturation',
          path: '/billing-client-list',
          icon: <ReceiptLongIcon sx={{ fontSize: 20 }} />,
        },
        {
          label: 'Factures en cours',
          path: '/pending-invoices',
          icon: <ReceiptLongIcon sx={{ fontSize: 20 }} />,
        },
        {
          label: 'Factures prêtes',
          path: '/pending-invoices-preview',
          icon: <ReceiptLongIcon sx={{ fontSize: 20 }} />,
        },
        {
          label: 'Prochaine facturation',
          path: '/next-period-preview',
          icon: <ReceiptLongIcon sx={{ fontSize: 20 }} />,
        },
        {
          label: 'Historique Factures',
          path: '/invoice-batches',
          icon: <ReceiptLongIcon sx={{ fontSize: 20 }} />,
        },
      ],
    },
    {
      title: 'Statistiques',
      icon: <AssessmentIcon />,
      color: '#9B59B6',
      items: [
        {
          label: 'Statistiques CG Mensuelles',
          path: '/cg-monthly-stats',
          icon: <AssessmentIcon sx={{ fontSize: 20 }} />,
        },
        {
          label: 'Statistiques DA Mensuelles',
          path: '/da-monthly-stats',
          icon: <AssessmentIcon sx={{ fontSize: 20 }} />,
        },
      ],
    },
  ];

  const handleNavigate = (path: string) => {
    navigate(path);
    setIsOpen(false);
  };

  return (
    <>
      <Tooltip title="Administration" placement="bottom">
        <IconButton
          onClick={() => setIsOpen(true)}
          sx={{
            ml: 2,
            color: '#fdd15f',
            border: '2px solid #fdd15f',
            borderRadius: '12px',
            padding: '8px',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: '-100%',
              width: '100%',
              height: '100%',
              background: 'linear-gradient(90deg, transparent, rgba(253, 209, 95, 0.3), transparent)',
              transition: 'left 0.5s',
            },
            '&:hover': {
              backgroundColor: 'rgba(253, 209, 95, 0.1)',
              transform: 'scale(1.05) rotate(5deg)',
              boxShadow: '0 0 20px rgba(253, 209, 95, 0.3)',
              '&::before': {
                left: '100%',
              },
            },
            '&:active': {
              transform: 'scale(0.95)',
            },
          }}
        >
          <AdminPanelSettingsIcon sx={{ fontSize: 28 }} />
        </IconButton>
      </Tooltip>

      <Drawer
        anchor="right"
        open={isOpen}
        onClose={() => setIsOpen(false)}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 420 },
            background: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)',
            borderLeft: '1px solid rgba(253, 209, 95, 0.2)',
            boxShadow: '-10px 0 40px rgba(0, 0, 0, 0.5)',
          },
        }}
      >
        <Fade in={isOpen} timeout={300}>
          <Box sx={{ position: 'relative', height: '100%', display: 'flex', flexDirection: 'column' }}>
            {/* Header */}
            <Box
              sx={{
                p: 3,
                borderBottom: '1px solid rgba(253, 209, 95, 0.2)',
                background: 'linear-gradient(135deg, rgba(253, 209, 95, 0.1) 0%, rgba(253, 209, 95, 0.05) 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexShrink: 0,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: '12px',
                    background: 'linear-gradient(135deg, #fdd15f 0%, #f0a500 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 4px 15px rgba(253, 209, 95, 0.3)',
                  }}
                >
                  <AdminPanelSettingsIcon sx={{ color: '#000', fontSize: 28 }} />
                </Box>
                <Box>
                  <Typography
                    variant="h5"
                    sx={{
                      color: '#fdd15f',
                      fontWeight: 700,
                      letterSpacing: '-0.5px',
                    }}
                  >
                    Administration
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'rgba(253, 209, 95, 0.7)',
                      fontSize: '12px',
                    }}
                  >
                    Panneau de contrôle
                  </Typography>
                </Box>
              </Box>
              <IconButton
                onClick={() => setIsOpen(false)}
                sx={{
                  color: '#fdd15f',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    backgroundColor: 'rgba(253, 209, 95, 0.1)',
                    transform: 'rotate(90deg)',
                  },
                }}
              >
                <CloseIcon />
              </IconButton>
            </Box>

            {/* Menu Categories */}
            <Box sx={{ p: 2, pb: 10, flex: 1, overflowY: 'auto' }}>
              {menuCategories.map((category, categoryIndex) => (
                <Fade
                  key={categoryIndex}
                  in={isOpen}
                  timeout={500 + categoryIndex * 200}
                  style={{ transitionDelay: `${categoryIndex * 100}ms` }}
                >
                  <Box sx={{ mb: 3 }}>
                    {/* Category Header */}
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        mb: 1.5,
                        px: 1,
                      }}
                    >
                      <Box
                        sx={{
                          width: 36,
                          height: 36,
                          borderRadius: '8px',
                          background: `linear-gradient(135deg, ${category.color} 0%, ${category.color}CC 100%)`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          boxShadow: `0 4px 15px ${category.color}40`,
                        }}
                      >
                        {category.icon}
                      </Box>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          color: '#fdd15f',
                          fontWeight: 600,
                          fontSize: '14px',
                          textTransform: 'uppercase',
                          letterSpacing: '1px',
                        }}
                      >
                        {category.title}
                      </Typography>
                    </Box>

                    {/* Category Items */}
                    <List sx={{ py: 0 }}>
                      {category.items.map((item, itemIndex) => (
                        <ListItem
                          key={itemIndex}
                          disablePadding
                          sx={{
                            mb: 0.5,
                            animation: `slideIn 0.3s ease-out ${categoryIndex * 100 + itemIndex * 50}ms backwards`,
                            '@keyframes slideIn': {
                              from: {
                                opacity: 0,
                                transform: 'translateX(20px)',
                              },
                              to: {
                                opacity: 1,
                                transform: 'translateX(0)',
                              },
                            },
                          }}
                        >
                          <ListItemButton
                            onClick={() => handleNavigate(item.path)}
                            sx={{
                              borderRadius: '12px',
                              py: 1.5,
                              px: 2,
                              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                              position: 'relative',
                              overflow: 'hidden',
                              '&::before': {
                                content: '""',
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '4px',
                                height: '100%',
                                background: category.color,
                                transform: 'scaleY(0)',
                                transition: 'transform 0.3s ease',
                              },
                              '&:hover': {
                                backgroundColor: 'rgba(253, 209, 95, 0.08)',
                                transform: 'translateX(8px)',
                                '&::before': {
                                  transform: 'scaleY(1)',
                                },
                              },
                              '&:active': {
                                transform: 'translateX(4px) scale(0.98)',
                              },
                            }}
                          >
                            <ListItemIcon
                              sx={{
                                minWidth: 40,
                                color: category.color,
                                transition: 'transform 0.3s ease',
                                '.MuiListItemButton-root:hover &': {
                                  transform: 'scale(1.1)',
                                },
                              }}
                            >
                              {item.icon}
                            </ListItemIcon>
                            <ListItemText
                              primary={item.label}
                              primaryTypographyProps={{
                                sx: {
                                  color: 'rgba(253, 209, 95, 0.9)',
                                  fontSize: '14px',
                                  fontWeight: 500,
                                  transition: 'color 0.3s ease',
                                  '.MuiListItemButton-root:hover &': {
                                    color: '#fdd15f',
                                  },
                                },
                              }}
                            />
                            <ChevronRightIcon
                              sx={{
                                color: 'rgba(253, 209, 95, 0.5)',
                                fontSize: 20,
                                transition: 'all 0.3s ease',
                                '.MuiListItemButton-root:hover &': {
                                  color: category.color,
                                  transform: 'translateX(4px)',
                                },
                              }}
                            />
                          </ListItemButton>
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                </Fade>
              ))}
            </Box>

            {/* Footer */}
            <Box
              sx={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                p: 2,
                borderTop: '1px solid rgba(253, 209, 95, 0.1)',
                background: 'linear-gradient(180deg, transparent 0%, rgba(0, 0, 0, 0.3) 100%)',
                pointerEvents: 'none',
                flexShrink: 0,
                zIndex: 1,
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  color: 'rgba(253, 209, 95, 0.5)',
                  textAlign: 'center',
                  display: 'block',
                  fontSize: '11px',
                  pointerEvents: 'auto',
                }}
              >
                Accès administrateur • Skipcar
              </Typography>
            </Box>
          </Box>
        </Fade>
      </Drawer>
    </>
  );
};

export default AdminDrawer;
